import { ReactNode } from 'react'
import {
  unfoldResponsesForLane,
  type MonadicLaneScopeFact,
} from '../../lib/monadicLaneScope'
import {
  disambiguatedAssetReportLabels,
  type AssetReportContextItem,
} from './asset-report-label'

interface LaneMergedSectionsProps {
  question: any
  responses: Array<any>
  monadicLaneScope?: MonadicLaneScopeFact | null
  assetContextItems?: Array<AssetReportContextItem>
  testIdPrefix: string
  /**
   * Copy for a lane nobody answered. The parent owns i18n so deck-ui
   * never hardcodes English here.
   */
  emptyLabel?: string
  children: (unfolded: Array<any>, assetId: string) => ReactNode
}

/**
 * One native chart per lane, after splitting by the study-level fact.
 *
 * `null` when the question is not lane-merged, so the caller keeps its
 * ordinary single-series path. A lane nobody answered still renders, with
 * the empty-lane copy rather than a native chart that reads as 0%.
 */
export function LaneMergedSections(props: LaneMergedSectionsProps) {
  const {
    question,
    responses,
    monadicLaneScope,
    assetContextItems,
    testIdPrefix,
    emptyLabel,
    children,
  } = props

  const lanes = unfoldResponsesForLane(question, responses, monadicLaneScope)
  if (lanes.length === 0) return null

  const labels = disambiguatedAssetReportLabels(
    lanes.map((lane) => lane.assetId),
    assetContextItems,
  )

  return (
    <div data-testid={`${testIdPrefix}-lane-merged`} className="flex flex-col gap-8">
      {lanes.map(({ assetId, responses: unfolded }) => (
        <div
          key={assetId}
          data-testid={`${testIdPrefix}-lane-${assetId}`}
          className="flex flex-col gap-3"
        >
          <p
            className="text-xs text-muted-foreground"
            data-testid={`${testIdPrefix}-lane-label-${assetId}`}
          >
            {labels[assetId] || assetId}
          </p>
          {unfolded.length === 0 ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid={`${testIdPrefix}-empty-lane-${assetId}`}
            >
              {emptyLabel}
            </p>
          ) : (
            children(unfolded, assetId)
          )}
        </div>
      ))}
    </div>
  )
}
